import React, { useState } from 'react';
import { FiPlus, FiTrash2, FiUpload, FiVolume2 } from 'react-icons/fi';
import { API_ORIGIN } from '../../../../services/api';

const ListeningBlockEditor = ({ block, onUpdate, availableVoices }) => {
    const [generatingAudio, setGeneratingAudio] = useState(false);
    const [uploadingAudio, setUploadingAudio] = useState(false);
    
    const description = block.description || '';
    const audioText = block.audioText || '';
    const audioUrl = block.audioUrl || '';
    const voiceId = block.voiceId || 'en-US-JennyNeural';
    const questions = block.questions || [];

    const handleGenerateAudio = async () => {
        if (!audioText.trim()) {
            alert('Введите текст для озвучивания');
            return;
        }

        try {
            setGeneratingAudio(true);
            
            const response = await fetch(`${API_ORIGIN}/api/tts/generate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    text: audioText,
                    voiceId: voiceId
                })
            });

            if (!response.ok) {
                throw new Error('Ошибка генерации аудио');
            }

            const data = await response.json();
            onUpdate({ audioUrl: `${API_ORIGIN}${data.audioUrl}` });
            
        } catch (error) {
            console.error('Ошибка генерации аудио:', error);
            alert('Ошибка при генерации аудио');
        } finally {
            setGeneratingAudio(false);
        }
    };

    const handleAudioUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Проверяем тип файла
        if (!file.type.startsWith('audio/')) {
            alert('Пожалуйста, загрузите аудиофайл');
            return;
        }

        try {
            setUploadingAudio(true);
            
            const formData = new FormData();
            formData.append('audio', file);

            const response = await fetch(`${API_ORIGIN}/api/admin/upload-audio`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
                },
                body: formData
            });

            if (!response.ok) {
                throw new Error('Ошибка загрузки аудио');
            }

            const data = await response.json();
            onUpdate({ audioUrl: `${API_ORIGIN}${data.audioUrl}` });
            alert('Аудио успешно загружено!');
            
        } catch (error) {
            console.error('Ошибка загрузки аудио:', error);
            alert('Ошибка при загрузке аудио');
        } finally {
            setUploadingAudio(false);
        }
    };

    const addQuestion = () => {
        onUpdate({
            questions: [...questions, {
                questionText: '',
                correctAnswer: 'true',
                explanation: '',
                points: 1
            }]
        });
    };

    const removeQuestion = (index) => {
        onUpdate({
            questions: questions.filter((_, i) => i !== index)
        });
    };

    const updateQuestion = (index, field, value) => {
        onUpdate({
            questions: questions.map((q, i) => 
                i === index ? { ...q, [field]: value } : q
            )
        });
    };

    return (
        <div style={{ display: 'grid', gap: '20px' }}>
            {/* Описание задания */}
            <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Описание задания</label>
                <textarea
                    value={description}
                    onChange={(e) => onUpdate({ description: e.target.value })}
                    rows={3}
                    placeholder="Например: Прослушайте аудио и определите, являются ли утверждения правдой, ложью или информация не дана"
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                />
            </div>

            {/* Аудио */}
            <div style={{ padding: '20px', background: '#f9f9f9', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                <h4 style={{ marginTop: 0 }}>Аудио</h4>
                
                <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Текст для озвучивания</label>
                    <p style={{ fontSize: '12px', color: '#999', marginBottom: '8px' }}>
                        Введите текст на английском. Он будет озвучен, но не будет виден студенту
                    </p>
                    <textarea
                        value={audioText}
                        onChange={(e) => onUpdate({ audioText: e.target.value })}
                        rows={5}
                        placeholder="The weather in London is usually cloudy and rainy..."
                        style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', marginBottom: '10px' }}
                    />
                    
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <select
                            value={voiceId}
                            onChange={(e) => onUpdate({ voiceId: e.target.value })}
                            style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '6px', flex: 1 }}
                        >
                            {availableVoices.map(voice => (
                                <option key={voice.ShortName} value={voice.ShortName}>
                                    {voice.DisplayName}
                                </option>
                            ))}
                        </select>
                        
                        <button
                            type="button"
                            onClick={handleGenerateAudio}
                            disabled={generatingAudio || !audioText.trim()}
                            style={{
                                padding: '8px 16px',
                                background: '#4a90e2',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: (generatingAudio || !audioText.trim()) ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                opacity: (generatingAudio || !audioText.trim()) ? 0.6 : 1
                            }}
                        >
                            <FiVolume2 /> {generatingAudio ? 'Генерация...' : 'Сгенерировать аудио'}
                        </button>
                    </div>
                </div>

                <div style={{ borderTop: '1px solid #ddd', paddingTop: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                        Или загрузить готовое аудио
                    </label>
                    
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
                        <input
                            type="file"
                            accept="audio/*"
                            onChange={handleAudioUpload}
                            disabled={uploadingAudio}
                            style={{ display: 'none' }}
                            id="audio-upload-input"
                        />
                        <label
                            htmlFor="audio-upload-input"
                            style={{
                                padding: '8px 16px',
                                background: uploadingAudio ? '#ccc' : '#52c41a',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: uploadingAudio ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                fontWeight: '500'
                            }}
                        >
                            <FiUpload /> {uploadingAudio ? 'Загрузка...' : 'Загрузить файл'}
                        </label>
                        
                        {audioUrl && (
                            <button
                                type="button"
                                onClick={() => onUpdate({ audioUrl: '' })}
                                style={{
                                    padding: '8px 16px',
                                    background: '#ff4d4f',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                }}
                            >
                                Удалить аудио
                            </button>
                        )}
                    </div>
                    
                    {audioUrl && (
                        <div style={{ marginTop: '10px' }}>
                            <audio controls style={{ width: '100%' }}>
                                <source src={audioUrl} type="audio/mpeg" />
                            </audio>
                        </div>
                    )}
                </div>
            </div>

            {/* Вопросы True/False/Not Given */}
            <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h4 style={{ margin: 0 }}>Вопросы True/False/Not Given ({questions.length})</h4>
                    <button
                        type="button"
                        onClick={addQuestion}
                        style={{
                            padding: '8px 16px',
                            background: '#52c41a',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontWeight: '500'
                        }}
                    >
                        <FiPlus /> Добавить вопрос
                    </button>
                </div>

                {questions.length === 0 && (
                    <div style={{
                        padding: '40px',
                        textAlign: 'center',
                        background: '#f9f9f9',
                        borderRadius: '8px',
                        color: '#999'
                    }}>
                        Нет вопросов. Добавьте вопросы после прослушивания
                    </div>
                )}

                {questions.map((question, index) => (
                    <div
                        key={index}
                        style={{
                            padding: '15px',
                            background: '#f9f9f9',
                            borderRadius: '8px',
                            marginBottom: '10px',
                            border: '1px solid #e0e0e0'
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                            <span style={{ fontWeight: '600' }}>Вопрос {index + 1}</span>
                            <button
                                type="button"
                                onClick={() => removeQuestion(index)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#ff4d4f',
                                    cursor: 'pointer'
                                }}
                            >
                                <FiTrash2 />
                            </button>
                        </div>

                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', fontWeight: '600' }}>
                                Утверждение
                            </label>
                            <textarea
                                value={question.questionText}
                                onChange={(e) => updateQuestion(index, 'questionText', e.target.value)}
                                rows={2}
                                placeholder="Например: The weather in London is always sunny"
                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                            />
                        </div>

                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', fontWeight: '600' }}>
                                Правильный ответ
                            </label>
                            <div style={{ display: 'flex', gap: '15px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                    <input
                                        type="radio"
                                        name={`answer-${index}`}
                                        checked={question.correctAnswer === 'true'}
                                        onChange={() => updateQuestion(index, 'correctAnswer', 'true')}
                                    />
                                    <span>True (Правда)</span>
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                    <input
                                        type="radio"
                                        name={`answer-${index}`}
                                        checked={question.correctAnswer === 'false'}
                                        onChange={() => updateQuestion(index, 'correctAnswer', 'false')}
                                    />
                                    <span>False (Ложь)</span>
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                    <input
                                        type="radio"
                                        name={`answer-${index}`}
                                        checked={question.correctAnswer === 'not_given'}
                                        onChange={() => updateQuestion(index, 'correctAnswer', 'not_given')}
                                    />
                                    <span>Not Given (Не дано)</span>
                                </label>
                            </div>
                        </div>

                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', fontWeight: '600' }}>
                                Объяснение (необязательно)
                            </label>
                            <textarea
                                value={question.explanation}
                                onChange={(e) => updateQuestion(index, 'explanation', e.target.value)}
                                rows={2}
                                placeholder="Объяснение правильного ответа"
                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', fontWeight: '600' }}>
                                Баллы за вопрос
                            </label>
                            <input
                                type="number"
                                value={question.points || 1}
                                onChange={(e) => updateQuestion(index, 'points', parseInt(e.target.value) || 1)}
                                min="1"
                                style={{ width: '100px', padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ListeningBlockEditor;
